import React, { useState } from "react";
import { Send, Loader2, FileText, Bike } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const INPUT = "w-full bg-ink border border-zinc-700 px-4 py-3 text-white placeholder-zinc-600 focus:border-neon-green outline-none text-sm";

export function JobApplyForm({ city = "", jobTitle = "", sourceSlug = "" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city, licence: "B", vehicle: "", availability: "", message: "" });
  const [cv, setCv] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onCv = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setCv(null); return; }
    if (f.type !== "application/pdf") { toast.error("Le CV doit être un PDF"); e.target.value = ""; return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("CV trop lourd (5 Mo max)"); e.target.value = ""; return; }
    setCv(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.phone.trim().length < 6 || form.city.trim().length < 2) { toast.error("Nom, téléphone et ville sont obligatoires"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("source_slug", sourceSlug);
      if (cv) fd.append("cv", cv);
      await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (sent) return (
    <div data-testid="job-apply-success" className="border border-neon-green bg-neon-green/10 p-6 text-center my-10">
      <p className="font-anton text-2xl uppercase text-neon-green">Candidature envoyée ✅</p>
      <p className="text-zinc-300 text-sm mt-2">Merci {form.name} ! L'équipe STUFF te recontacte par WhatsApp ou téléphone sous 48h.</p>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="job-apply-form" className="border border-neon-magenta/60 bg-neon-magenta/5 p-5 sm:p-6 my-10 space-y-3">
      <p className="font-graffiti text-neon-magenta text-lg">On recrute</p>
      <h2 className="font-anton text-2xl uppercase text-white flex items-center gap-2"><Bike className="h-6 w-6 text-neon-green" /> Postuler{city ? ` — ${city}` : ""}</h2>
      {jobTitle && <p className="text-zinc-400 text-sm">{jobTitle} · sans créer de compte, réponse sous 48h.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid="apply-name" value={form.name} onChange={upd("name")} placeholder="Nom complet *" maxLength={80} required className={INPUT} />
        <input data-testid="apply-phone" value={form.phone} onChange={upd("phone")} placeholder="Téléphone / WhatsApp *" maxLength={25} inputMode="tel" required className={INPUT} />
        <input data-testid="apply-email" type="email" value={form.email} onChange={upd("email")} placeholder="Email" maxLength={120} className={INPUT} />
        <input data-testid="apply-city" value={form.city} onChange={upd("city")} placeholder="Ville *" maxLength={60} required className={INPUT} />
        <select data-testid="apply-licence" value={form.licence} onChange={upd("licence")} className={INPUT}>
          <option value="B">Permis B</option>
          <option value="AM">Permis AM (scooter)</option>
          <option value="aucun">Aucun permis (vélo)</option>
        </select>
        <input data-testid="apply-vehicle" value={form.vehicle} onChange={upd("vehicle")} placeholder="Véhicule (vélo, scooter, voiture…)" maxLength={60} className={INPUT} />
      </div>
      <input data-testid="apply-availability" value={form.availability} onChange={upd("availability")} placeholder="Disponibilités (soirs, week-ends, temps plein…)" maxLength={200} className={INPUT} />
      <textarea data-testid="apply-message" value={form.message} onChange={upd("message")} placeholder="Un mot sur toi (optionnel)" rows={3} maxLength={2000} className={`${INPUT} resize-y`} />
      <label className="flex items-center gap-3 border border-dashed border-zinc-700 px-4 py-3 cursor-pointer hover:border-neon-green transition-colors">
        <FileText className="h-5 w-5 text-neon-green shrink-0" />
        <span className="text-sm text-zinc-300 flex-1 truncate">{cv ? cv.name : "Joindre mon CV (PDF, 5 Mo max) — optionnel"}</span>
        <input data-testid="apply-cv" type="file" accept="application/pdf" onChange={onCv} className="hidden" />
      </label>
      <button type="submit" data-testid="apply-submit" disabled={sending} className="w-full flex items-center justify-center gap-2 bg-neon-green text-black font-anton uppercase text-base py-3.5 glow-green hover:bg-neon-greenDark transition-colors disabled:opacity-50">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer ma candidature
      </button>
      <p className="text-zinc-600 text-[11px]">Réservé aux personnes majeures. Tes données servent uniquement au recrutement STUFF.</p>
    </form>
  );
}
